import React from 'react';
import { Outlet, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  Button,
  Masthead,
  MastheadBrand,
  MastheadContent,
  MastheadMain,
  MastheadToggle,
  Nav,
  NavItem,
  NavList,
  Page,
  PageSection,
  PageSidebar,
  PageSidebarBody,
  PageToggleButton,
} from '@patternfly/react-core';
import { BarsIcon, SunIcon, MoonIcon } from '@patternfly/react-icons';
import AppToolbar from './AppToolbar';
import { useAuth } from '../../context/AuthContext';
import { useBox } from '../../context/BoxContext';
import { useTheme } from '../../context/ThemeContext';

const THEME_ICONS = { light: SunIcon, dark: MoonIcon };
const THEME_LABELS = { light: 'Light', dark: 'Dark' };

const AppLayout: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const { selectedBoxId } = useBox();
  const { setMode, effectiveTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const isAdmin = user?.role === 'ADMIN';

  const toggleTheme = () => {
    setMode(effectiveTheme === 'light' ? 'dark' : 'light');
  };

  const ThemeIcon = THEME_ICONS[effectiveTheme];

  const masthead = (
    <Masthead>
      <MastheadMain>
        <MastheadToggle>
          <PageToggleButton variant="plain" aria-label="Global navigation">
            <BarsIcon />
          </PageToggleButton>
        </MastheadToggle>
        <MastheadBrand className="asafbox-masthead-brand">
          <span style={{ fontSize: '1.25rem', fontWeight: 700 }}>AsafBox</span>
        </MastheadBrand>
      </MastheadMain>
      <MastheadContent>
        <AppToolbar />
      </MastheadContent>
    </Masthead>
  );

  const sidebar = (
    <PageSidebar>
      <PageSidebarBody style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div className="asafbox-sidebar-brand">AsafBox</div>
        <Nav
          onSelect={(_e, result) => navigate(result.itemId as string)}
        >
          <NavList>
            {selectedBoxId && (
              <NavItem
                itemId="/weekly-view"
                isActive={location.pathname === '/weekly-view'}
              >
                Schedule
              </NavItem>
            )}
            {isAdmin && (
              <NavItem
                itemId="/users"
                isActive={location.pathname === '/users'}
              >
                Users
              </NavItem>
            )}
          </NavList>
        </Nav>
        <div style={{ marginTop: 'auto', padding: '1rem' }}>
          <Button
            variant="plain"
            onClick={toggleTheme}
            aria-label={`Theme: ${THEME_LABELS[effectiveTheme]}`}
            icon={<ThemeIcon />}
          >
            {THEME_LABELS[effectiveTheme]}
          </Button>
        </div>
      </PageSidebarBody>
    </PageSidebar>
  );

  return (
    <Page masthead={masthead} sidebar={sidebar} isManagedSidebar>
      <PageSection isFilled>
        <Outlet />
      </PageSection>
    </Page>
  );
};

export default AppLayout;
